import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray, } from '@angular/cdk/drag-drop';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { LearningSectionJsonModel } from '../../learning-section.json.model';
import { LearningSectionContainerJsonModel } from '../learning-section-container.json.model';
import { Store } from '@ngxs/store';
import {
  LearningSectionContainerActions
} from '../../ngxs/learning-section-container/learning-section-container.actions';
import { LearningSectionState } from '../../ngxs/learning-section/learning-section.state';
import { combineLatest, filter, of, take } from 'rxjs';
import { LearningSectionActions } from '../../ngxs/learning-section/learning-section.actions';
import { getLearningSectionContainerContentType } from '../../learning-section-container-content-type.layout.enum';
import { environment } from '../../../../environments/environment';
import { LearningSectionLayoutState } from '../../ngxs/learning-section-layout/learning-section-layout.state';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LearningSectionLayoutJsonModel } from '../../learning-section-layout/learning-section-layout.json.model';
import { LearningSectionLayoutActions } from '../../ngxs/learning-section-layout/learning-section-layout.actions';
import { IFilters } from '@datx/jsonapi/interfaces/IFilters';

@Component({
  selector: 'pixl-learning-section-container-edit',
  imports: [
    CdkDropList,
    CdkDrag,
    MatButton,
    MatButtonModule,
    MatIcon,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatDialogModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './learning-section-container-edit.component.html',
  styleUrl: './learning-section-container-edit.component.scss'
})
export class LearningSectionContainerEditComponent implements OnInit {
  private store = inject(Store);
  cdr = inject(ChangeDetectorRef);
  dialogRef = inject(MatDialogRef);
  private destroyRef = inject(DestroyRef);

  public data = inject<LearningSectionJsonModel & { filters?: IFilters }>(MAT_DIALOG_DATA);

  learningSection$ = this.store.select(LearningSectionState.getLearningSection);
  learningSectionContainer$ = this.store.select(LearningSectionState.getLearningSectionContainers);
  layouts$ = this.store.select(LearningSectionLayoutState.getLearningSectionLayouts);

  selectedLayoutOption!: LearningSectionLayoutJsonModel;
  columnNumber!: number;

  ngOnInit() {
    this.store.dispatch(new LearningSectionLayoutActions.Fetch({
      pageNumber: 1,
      pageSize: -1,
      skipStoreDispatch: true
    }));
    combineLatest([
      this.layouts$.pipe(filter(layouts => layouts.length > 0), take(1)),
      of(this.data)
    ]).subscribe(([layouts, data]) => {
      const defaultLayout = layouts.find(l => l.percentageSplit === 100);
      const selectedLayout = data.learningSectionLayout ?
        layouts.find(l => l.id === data.learningSectionLayout.id) ?? defaultLayout :
        defaultLayout;
      if (selectedLayout) {
        this.setLayout(selectedLayout);
      }
    });
  }

  constructor() {
    this.destroyRef.onDestroy(() => this.saveOnExit());
  }

  addContainer() {
    this.learningSectionContainer$
      .pipe(take(1))
      .subscribe(() => {
        const newLearningSectionContainer = new LearningSectionContainerJsonModel();
        newLearningSectionContainer.title = 'Container';
        this.store.dispatch(new LearningSectionContainerActions.AddRaw(newLearningSectionContainer));
      });
  }

  removeItem(container: LearningSectionContainerJsonModel) {
    this.store.dispatch(new LearningSectionContainerActions.Delete(container));
  }

  drop(event: CdkDragDrop<LearningSectionContainerJsonModel[]>) {
    this.learningSectionContainer$.pipe(take(1)).subscribe(containers => {
      if (containers) {
        const draggedContainer = event.item.data;
        const previousIndex = containers.findIndex(c => c.id === draggedContainer.id);
        const currentIndex = event.currentIndex;

        if (previousIndex === currentIndex) return;

        // reorder containers locally
        const updatedContainers = [...containers];
        moveItemInArray(updatedContainers, previousIndex, currentIndex);

        this.learningSection$.pipe(take(1)).subscribe(learningSection => {
          if (learningSection instanceof LearningSectionJsonModel) {
            learningSection.learningSectionContainers = updatedContainers;
            this.store.dispatch(new LearningSectionActions.EditRaw(learningSection));
          }
        });
      }
    });
  }

  saveOnExit() {
    this.learningSectionContainer$.pipe(take(1)).subscribe(containers => {
      if (containers) {
        containers.forEach((container) => {
          if (Array.isArray(container.learningSections)) {
            container.learningSections = [this.data];
          }

          this.store.dispatch(new LearningSectionContainerActions.EditRaw(container));
        });
      }
    });
  }

  onChangeLayout(selectedLayout: LearningSectionLayoutJsonModel): void {
    this.learningSection$.pipe(
      filter(section => !!section),
      take(1)
    ).subscribe(learningSection => {
      if (learningSection instanceof LearningSectionJsonModel) {
        if (learningSection.learningSectionLayout === selectedLayout) return;

        learningSection.learningSectionLayout = selectedLayout;

        this.columnNumber = selectedLayout.columns;
        this.store.dispatch(new LearningSectionActions.EditRaw(learningSection, this.data.filters));
      }
    });
  }

  private setLayout(selectedLayout: LearningSectionLayoutJsonModel) {
    this.selectedLayoutOption = selectedLayout;
    this.columnNumber = selectedLayout.columns;
  }

  get gridTemplateColumns(): string {
    const layout = this.selectedLayoutOption;
    if (!layout || layout.columns === 1 || Number(layout.percentageSplit) === 100) {
      return '100%';
    }

    switch (Number(layout.percentageSplit)) {
      case 25: return '25% 75%';
      case 50: return '50% 50%';
      case 75: return '75% 25%';
      case 80: return '80% 20%';
      default: return '50% 50%'; // fallback for unknown values with 2 columns
    }
  }

  editLearningSectionSingleContainer(container: LearningSectionContainerJsonModel) {
    this.store.dispatch(new LearningSectionContainerActions.EditSingle(container));
  }

  getPreviewLink(): string | null {
    const learningSectionPreviewUrl = '/learning-section-preview/' + this.data.id;
    return environment.learningSectionPreviewHost + learningSectionPreviewUrl + '?token=' + environment.learningSectionPreviewToken;
  }

  onClose(): void {
    this.saveOnExit();
    this.dialogRef.close();
  }

  protected readonly getLearningSectionContainerContentType = getLearningSectionContainerContentType;
}
